import { ReactElement, useState } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useUSGA } from '../../contextProviders/USGAProvider';

interface USGALoginControlProps {
  onLoginSuccess?: () => void;
}

export default function USGALoginControl({ onLoginSuccess }: USGALoginControlProps): ReactElement {
  const { isUSGALoggedIn, usgaLogin, usgaLogout } = useUSGA();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async () => {
    setIsLoading(true);
    setError(null);
    const result = await usgaLogin(email, password);
    setIsLoading(false);
    if (result.success) {
      setEmail('');
      setPassword('');
      onLoginSuccess?.();
    } else {
      setError(result.error || 'Login failed');
    }
  };

  if (isUSGALoggedIn) {
    return (
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Alert severity="success" sx={{ flexGrow: 1 }}>Logged in to GHIN Staging</Alert>
        <Button variant="outlined" onClick={usgaLogout}>Logout</Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="titleSmall">GHIN Staging Login</Typography>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            size="small"
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            onKeyDown={(e) => e.key === 'Enter' && email && password && handleLogin()}
          />
          <TextField
            size="small"
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            onKeyDown={(e) => e.key === 'Enter' && email && password && handleLogin()}
          />
          <Button
            variant="contained"
            onClick={handleLogin}
            disabled={isLoading || !email || !password}
          >
            {isLoading ? <CircularProgress size="1.5rem" /> : 'Login'}
          </Button>
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
    </Stack>
  );
}
